import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Breadcrumbs from "../../component/breadcrumbs/breadcrumbs";
import "./about.css";
import videoHero from "../../assets/new-hero.mp4";
import { useLanguage } from "../../i18n/LanguageContext";

const About = () => {
  const { tr } = useLanguage();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <div className="about-page">
      {/* Hero Section with Video Background */}
      <section className="about-hero">
        <div className="about-hero-video-wrapper">
          <video
            className="about-hero-video"
            src={videoHero}
            autoPlay
            loop
            muted
            playsInline
          />
          <div className="about-hero-overlay" />
        </div>
        <div className="about-hero-breadcrumbs">
          <Breadcrumbs />
        </div>
        <motion.div
          className="about-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: isMobile ? 0.3 : 0.6, delay: isMobile ? 0.05 : 0.2 }}
        >
          <motion.h1
            className="about-hero-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isMobile ? 0.3 : 0.5, delay: isMobile ? 0.1 : 0.3 }}
          >
            {tr("About Us", "Tentang Kami")}
          </motion.h1>
          <motion.p
            className="about-hero-mission"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isMobile ? 0.3 : 0.5, delay: isMobile ? 0.15 : 0.5 }}
          >
            {tr(
              "Our mission is to provide businesses with the AI tools they need to thrive in today's market.",
              "Misi kami adalah menyediakan tools AI yang dibutuhkan bisnis untuk berkembang di pasar saat ini."
            )}
          </motion.p>
        </motion.div>
      </section>

      {/* Our Story Section */}
      <section className="about-story">
        <div className="about-story-container">
          <motion.h2
            className="about-story-title"
            initial={{ opacity: 0, y: 20 }}
            {...(isMobile 
              ? { animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } }
              : { whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.5 }, viewport: { once: true } }
            )}
          >
            {tr("Our Story", "Cerita Kami")}
          </motion.h2>
          
          <div className="about-story-grid">
            {/* History Column */}
            <motion.div
              className="about-story-column"
              initial={{ opacity: 0, y: 30 }}
              {...(isMobile 
                ? { animate: { opacity: 1, y: 0 }, transition: { delay: 0, duration: 0.3 } }
                : { whileInView: { opacity: 1, y: 0 }, transition: { delay: 0.1, duration: 0.5 }, viewport: { once: true } }
              )}
            >
              <h3 className="about-story-column-title">
                {tr("History Of The Company", "Sejarah Perusahaan")}
              </h3>
              <p className="about-story-column-text">
                {tr(
                  "Metagent was born from recognizing a critical gap in how modern solopreneurs work. We observed that employees, freelancers, influencers, and small business owners spend too much time on repetitive tasks, scattered communication, and manual coordination, time that should be spent on what truly matters: growth and innovation.",
                  "Metagent lahir dari pengamatan terhadap celah besar dalam cara kerja solopreneur modern. Kami melihat karyawan, freelancer, influencer, dan pemilik bisnis kecil menghabiskan terlalu banyak waktu pada tugas berulang, komunikasi yang terpencar, dan koordinasi manual—padahal waktunya bisa dipakai untuk hal yang benar‑benar penting: growth dan inovasi."
                )}
              </p>
              <p className="about-story-column-text">
                {tr(
                  "Founded with a vision to transform how individuals work, Metagent combines intelligent automation with personalized AI agents that adapt to each user's unique workflow, tools, and priorities.",
                  "Dengan visi mentransformasi cara orang bekerja, Metagent menggabungkan automasi cerdas dengan AI agent yang dipersonalisasi sesuai workflow, tools, dan prioritas tiap pengguna."
                )}
              </p>
            </motion.div>

            {/* Mission Column */}
            <motion.div
              className="about-story-column"
              initial={{ opacity: 0, y: 30 }}
              {...(isMobile 
                ? { animate: { opacity: 1, y: 0 }, transition: { delay: 0.05, duration: 0.3 } }
                : { whileInView: { opacity: 1, y: 0 }, transition: { delay: 0.2, duration: 0.5 }, viewport: { once: true } }
              )}
            >
              <h3 className="about-story-column-title">{tr("Mission", "Misi")}</h3>
              <p className="about-story-column-text">
                {tr(
                  "Our mission is to empower solopreneurs by providing AI agents that handle repetitive tasks, unify knowledge and context, and support smart decision-making, enabling them to focus on their core work and scale their operations without adding complexity.",
                  "Misi kami adalah memberdayakan solopreneur dengan AI agent yang menangani tugas berulang, menyatukan knowledge dan context, serta mendukung pengambilan keputusan cerdas, sehingga mereka fokus pada core work dan scaling tanpa menambah kompleksitas."
                )}
              </p>
              <p className="about-story-column-text">
                {tr(
                  "We believe that AI should work the way you already work, not the other way around. Each Metagent AI agent is customizable, extensible, and evolves with your needs.",
                  "Kami percaya AI harus mengikuti caramu bekerja, bukan sebaliknya. Setiap Metagent AI agent bisa dikustomisasi, diperluas, dan berkembang sesuai kebutuhanmu."
                )}
              </p>
            </motion.div>

            {/* Values Column */}
            <motion.div
              className="about-story-column"
              initial={{ opacity: 0, y: 30 }}
              {...(isMobile 
                ? { animate: { opacity: 1, y: 0 }, transition: { delay: 0.1, duration: 0.3 } }
                : { whileInView: { opacity: 1, y: 0 }, transition: { delay: 0.3, duration: 0.5 }, viewport: { once: true } }
              )}
            >
              <h3 className="about-story-column-title">{tr("Company Values", "Nilai Perusahaan")}</h3>
              <p className="about-story-column-text">
                <strong>{tr("Adaptability:", "Adaptabilitas:")}</strong>{" "}
                {tr(
                  "We build solutions that evolve with your work, ensuring long-term value without operational complexity.",
                  "Kami membangun solusi yang berkembang bersama pekerjaanmu, memastikan nilai jangka panjang tanpa kompleksitas operasional."
                )}
              </p>
              <p className="about-story-column-text">
                <strong>{tr("Personalization:", "Personalisasi:")}</strong>{" "}
                {tr(
                  "Every AI agent is tailored to match your workflow, tools, and priorities, no one-size-fits-all approach.",
                  "Setiap AI agent disesuaikan dengan workflow, tools, dan prioritasmu, tanpa pendekatan satu‑untuk‑semua."
                )}
              </p>
              <p className="about-story-column-text">
                <strong>{tr("Empowerment:", "Pemberdayaan:")}</strong>{" "}
                {tr(
                  "We enable solopreneurs to work smarter, not harder, by automating what can be automated and amplifying what can't.",
                  "Kami membantu solopreneur bekerja lebih cerdas, bukan lebih keras, dengan mengotomasi yang bisa diautomasi dan memperkuat yang tidak."
                )}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="about-what-we-do">
        <div className="about-what-we-do-container">
          <motion.h2
            className="about-what-we-do-title"
            initial={{ opacity: 0, y: 20 }}
            {...(isMobile 
              ? { animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } }
              : { whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.5 }, viewport: { once: true } }
            )}
          >
            {tr("What We Do", "Apa yang Kami Lakukan")}
          </motion.h2>
          <div className="about-what-we-do-grid">
            {[
              {
                title: tr("Intelligent Task Automation", "Automasi Tugas Cerdas"),
                text: tr(
                  "Metagent handles repetitive tasks automatically, capturing action items, following up, and keeping work moving without manual intervention.",
                  "Metagent menangani tugas berulang secara otomatis, menangkap action item, follow‑up, dan menjaga pekerjaan tetap berjalan tanpa intervensi manual."
                )
              },
              {
                title: tr("Unified Knowledge & Context", "Knowledge & Context Terpadu"),
                text: tr(
                  "All information is organized in one place, so nothing gets lost across emails, chats, or tools, your AI agent always understands the full context.",
                  "Semua informasi terorganisir di satu tempat, sehingga tidak ada yang hilang di email, chat, atau tools. AI agent selalu memahami konteks penuh."
                )
              },
              {
                title: tr("Smart Communication", "Komunikasi Cerdas"),
                text: tr(
                  "Metagent helps you communicate faster and more consistently by drafting replies, summaries, and follow-ups aligned with your tone and goals.",
                  "Metagent membantu komunikasi lebih cepat dan konsisten dengan draft balasan, ringkasan, dan follow‑up sesuai tone dan tujuan."
                )
              },
              {
                title: tr("Decision & Insight Support", "Dukungan Keputusan & Insight"),
                text: tr(
                  "Instead of raw data and long threads, Metagent delivers clear summaries and recommendations to help you decide and act quickly.",
                  "Alih‑alih data mentah dan thread panjang, Metagent memberi ringkasan dan rekomendasi jelas untuk membantu keputusan lebih cepat."
                )
              },
              {
                title: tr("Scalable Work Companion", "Partner Kerja yang Skalabel"),
                text: tr(
                  "Whether you're an employee, freelancer, influencer, or SMB owner, Metagent adapts to how you work, so productivity scales without adding complexity.",
                  "Baik kamu karyawan, freelancer, influencer, atau pemilik SMB, Metagent menyesuaikan caramu bekerja sehingga produktivitas naik tanpa kompleksitas."
                )
              },
              {
                title: tr("Customizable & Extensible", "Dapat Dikustomisasi & Diperluas"),
                text: tr(
                  "Your AI agent can be extended, adapted, and trained for specific tasks, roles, or business needs, without being limited to a fixed feature set.",
                  "AI agent bisa diperluas, diadaptasi, dan dilatih untuk tugas, role, atau kebutuhan bisnis spesifik tanpa batasan fitur tetap."
                )
              }
            ].map((item, i) => (
              <motion.div
                key={item.title}
                className="about-what-we-do-item"
                initial={{ opacity: 0, y: 30 }}
                {...(isMobile 
                  ? { animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.03, duration: 0.3 }, whileHover: { scale: 1.03, y: -4 } }
                  : { whileInView: { opacity: 1, y: 0 }, transition: { delay: i * 0.1, duration: 0.5 }, viewport: { once: true }, whileHover: { scale: 1.03, y: -4 } }
                )}
              >
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;

